import { Dialog, Portal, Progress, Tooltip } from "@ark-ui/react";
import {
  type BedAllocationFormInput,
  DELETION_FLAG,
  SENSOR_TYPE,
  type SensorKey,
  type UserKey,
  getBedAllocationFormSchema,
  parse,
} from "@packages/core";
import { useForm, useStore } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker } from "@tanstack/react-router";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuImport } from "react-icons/lu";

import {
  ButtonStyle,
  DropDownList,
  PageName,
  TableStyle,
  TypographyStyle,
  dialogStyles,
  progressStyles,
  showError,
  skeletonStyle,
  tooltipStyles,
} from "#components";
import {
  $alert,
  $confirm,
  allocateBed,
  removeResidentInfiniteQueryCache,
  removeSensorInfiniteQueryCache,
  removeSensorsCache,
  resetBedInfiniteQueries,
  useBedInfiniteQuery,
  useGetErrorMessage,
  useResidentInfiniteQuery,
  useSensorInfiniteQuery,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey/beds/allocation")({
  component: Component,
});

function Component() {
  const { facilityKey } = Route.useParams();

  const bedInfiniteQuery = useBedInfiniteQuery(
    {
      index: "facilityKey-deleted-gsi",
      condition: [
        ["facilityKey", "=", facilityKey],
        ["deleted", "=", DELETION_FLAG.DEFAULT],
      ],
    },
    { enabled: Boolean(facilityKey) },
  );
  const residentInfiniteQuery = useResidentInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const sensorInfiniteQuery = useSensorInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const formIdentifier = useId();
  const getErrorMessage = useGetErrorMessage();
  const { t } = useTranslation();
  const [progress, setProgress] = useState(100);

  useEffect(
    function () {
      // 有効なデータが対象の場合、全て取得する。
      if (bedInfiniteQuery.hasNextPage && !bedInfiniteQuery.isFetching) {
        bedInfiniteQuery.fetchNextPage();
      }
    },
    [bedInfiniteQuery],
  );

  useEffect(
    function () {
      // 有効なデータが対象の場合、全て取得する。
      if (residentInfiniteQuery.hasNextPage && !residentInfiniteQuery.isFetching) {
        residentInfiniteQuery.fetchNextPage();
      }
    },
    [residentInfiniteQuery],
  );

  useEffect(
    function () {
      // 有効なデータが対象の場合、全て取得する。
      if (sensorInfiniteQuery.hasNextPage && !sensorInfiniteQuery.isFetching) {
        sensorInfiniteQuery.fetchNextPage();
      }
    },
    [sensorInfiniteQuery],
  );

  const beds = useMemo(
    function () {
      return (
        bedInfiniteQuery.data?.pages
          .flatMap(function (page) {
            return page.beds;
          })
          .sort(function (a, b) {
            return (
              a.roomName.localeCompare(b.roomName) ||
              a.bedName.localeCompare(b.bedName) ||
              a.bedKey.localeCompare(b.bedKey)
            );
          }) ?? []
      );
    },
    [bedInfiniteQuery.data],
  );

  const residents = useMemo(
    function () {
      return (
        residentInfiniteQuery.data?.pages
          .flatMap(function (page) {
            return page.residents;
          })
          .sort(function (a, b) {
            let result = 0;
            if (result === 0 && a.pronunciationName && b.pronunciationName) {
              result =
                a.pronunciationName < b.pronunciationName
                  ? -1
                  : a.pronunciationName > b.pronunciationName
                    ? 1
                    : 0;
            }

            if (result === 0 && a.displayName && b.displayName) {
              result = a.displayName < b.displayName ? -1 : a.displayName > b.displayName ? 1 : 0;
            }

            if (result === 0) {
              result = a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
            }

            if (result === 0) {
              result = a.userKey < b.userKey ? -1 : a.userKey > b.userKey ? 1 : 0;
            }

            return result;
          }) ?? []
      );
    },
    [residentInfiniteQuery.data],
  );

  const sensors = useMemo(
    function () {
      return (
        sensorInfiniteQuery.data?.pages
          .flatMap(function (page) {
            return page.sensors;
          })
          .sort(function (a, b) {
            let result = 0;

            if (result === 0) {
              result = a.type < b.type ? -1 : a.type > b.type ? 1 : 0;
            }

            if (result === 0) {
              result = a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
            }

            if (result === 0) {
              result = a.sensorKey < b.sensorKey ? -1 : a.sensorKey > b.sensorKey ? 1 : 0;
            }

            return result;
          }) ?? []
      );
    },
    [sensorInfiniteQuery.data],
  );

  const schema = getBedAllocationFormSchema();
  const defaultValues = useMemo(
    function (): BedAllocationFormInput {
      return {
        allocations: beds.map(function (bed) {
          return { bedKey: bed.bedKey, sensorKey: bed.sensorKey, userKey: bed.userKey };
        }),
      };
    },
    [beds],
  );

  const form = useForm({
    canSubmitWhenInvalid: true,
    defaultValues,
    async onSubmit({ value: input }) {
      try {
        const output = parse(schema, input);
        console.debug("[onSubmit]", new Date(), { input, output });

        setProgress(0);

        for (let i = 0; i < output.allocations.length; i++) {
          const { bedKey, sensorKey, userKey } = output.allocations[i];

          try {
            await allocateBed({ bedKey, sensorKey: sensorKey ?? null, userKey: userKey ?? null });
            console.debug(
              bedKey + "に" + userKey + "(入居者)と" + sensorKey + "(センサー)を割り当てました。",
            );
          } catch (error) {
            console.error("ベッドの割り当てに失敗しました。", {
              bedKey,
              sensorKey,
              userKey,
              error,
            });
            showError(error);
            continue;
          }

          setProgress(((i + 1) / output.allocations.length) * 100);
        }

        setProgress(100);

        await $alert({
          color: "success",
          content: t("%BedAllocation.1%"),
          title: t("%Complete.0%"),
        });

        resetBedInfiniteQueries();
        removeResidentInfiniteQueryCache();
        removeSensorInfiniteQueryCache();
        removeSensorsCache();
      } catch (error) {
        showError(error);
        setProgress(100);
      }
    },
    onSubmitInvalid({ value: input }) {
      console.warn("[onSubmitInvalid]", new Date(), { input });
    },
    validators: { onSubmit: schema },
  });

  const allocations = useStore(form.store, (state) => state.values.allocations);

  const selectedUserKeys = useMemo(
    function () {
      const selectedUserKeys = new Set();

      for (const allocation of allocations) {
        if (allocation.userKey) {
          selectedUserKeys.add(allocation.userKey);
        }
      }

      return selectedUserKeys;
    },
    [allocations],
  );

  const selectedSensorKeys = useMemo(
    function () {
      const selectedSensorKeys = new Set();

      for (const allocation of allocations) {
        if (allocation.sensorKey) {
          selectedSensorKeys.add(allocation.sensorKey);
        }
      }

      return selectedSensorKeys;
    },
    [allocations],
  );

  {
    const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(form.store, (state) => state.isSubmitted);
    const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: t("%Form.1%"),
              no: t("%Stay.0%"),
              title: t("%Form.0%"),
              yes: t("%Leave.0%"),
            }))
          : false;
      },
    });
  }

  const dialogClasses = dialogStyles();
  const progressClasses = progressStyles();
  const tableClasses = TableStyle.raw({ foot: false });
  const tooltipClasses = tooltipStyles();
  const rowStyle = css.raw({
    gridTemplateColumns: "repeat(2, 120px) repeat(2, minmax(120px, 1fr))",
  });

  return (
    <main
      className={css({
        display: "flex",
        flexDirection: "column",
        padding: { smDown: "16px", sm: "32px 24px" },
      })}
      data-maximized
    >
      <PageName>{t("%RoomInformation.0%")}</PageName>

      <Dialog.Root open={progress !== 100}>
        <Portal>
          <Dialog.Backdrop className={dialogClasses.backdrop} />
          <Dialog.Positioner className={dialogClasses.positioner}>
            <Dialog.Content asChild>
              <Progress.Root className={progressClasses.root} value={progress}>
                <Progress.Label className={progressClasses.label}>
                  {t("%Processing.0%")}
                </Progress.Label>
                <Progress.Track className={progressClasses.track}>
                  <Progress.Range className={progressClasses.range} />
                </Progress.Track>
                <Progress.ValueText className={progressClasses.valueText} />
              </Progress.Root>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <h1 className={css({ fontSize: "18px", fontWeight: 600 })}>{t("%RoomInformation.0%")}</h1>

      <div
        className={css({
          alignItems: "flex-end",
          display: "flex",
          justifyContent: "space-between",
          marginTop: "16px",
        })}
      >
        <div></div>

        <div className={css({ alignItems: "center", display: "flex", gap: "8px" })}>
          <Link
            className={ButtonStyle({
              appearance: "outlined",
              borderRadius: "4px",
              color: "outlined",
              gap: "8px",
              fontSize: "14px",
              padding: "10px 16px",
            })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/beds/batch-allocation"
          >
            <LuImport />
            <span>{t("%Add.2%")}</span>
          </Link>

          <div
            className={css({
              borderColor: "border",
              borderLeftWidth: "1px",
              borderStyle: "solid",
              height: "stretch",
              maxHeight: "36px",
            })}
          />

          <Link
            className={ButtonStyle({
              appearance: "outlined",
              borderRadius: "4px",
              color: "outlined",
              gap: "8px",
              fontSize: "14px",
              padding: "10px 16px",
            })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/rooms/registration"
          >
            {t("%Cancel.0%")}
          </Link>

          <form.Subscribe
            selector={function (state) {
              return state.isSubmitting;
            }}
          >
            {function (submitting) {
              return (
                <button
                  className={ButtonStyle({
                    appearance: "contained",
                    borderRadius: "4px",
                    color: "primary",
                    gap: "8px",
                    fontSize: "14px",
                    padding: "10px 16px",
                  })}
                  disabled={submitting}
                  form={formIdentifier}
                  type="submit"
                >
                  {t("%Save.0%")}
                </button>
              );
            }}
          </form.Subscribe>
        </div>
      </div>

      <div className={css({ flexShrink: 1, marginTop: "8px", overflowX: "auto", width: "100%" })}>
        <form
          className={css(tableClasses.root, { height: "100%" })}
          id={formIdentifier}
          onSubmit={function (event) {
            event.preventDefault();
            form.handleSubmit();
          }}
        >
          <div className={css(tableClasses.head)}>
            <div className={css(tableClasses.headRow, rowStyle)}>
              <div
                className={TypographyStyle({
                  color: "primary/60",
                  ellipsis: true,
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: 1,
                })}
              >
                {t("%Room.0%")}
              </div>
              <div
                className={TypographyStyle({
                  color: "primary/60",
                  ellipsis: true,
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: 1,
                })}
              >
                {t("%Bed.0%")}
              </div>
              <div
                className={TypographyStyle({
                  color: "primary/60",
                  ellipsis: true,
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: 1,
                })}
              >
                {t("%Sensor.0%")}
              </div>
              <div
                className={TypographyStyle({
                  color: "primary/60",
                  ellipsis: true,
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: 1,
                })}
              >
                {t("%Resident.0%")}
              </div>
            </div>
          </div>

          <form.Field mode="array" name="allocations">
            {function (field) {
              return (
                <div className={css(tableClasses.body)}>
                  {field.state.value.map(function (allocation, index) {
                    const bed = beds.find(function (bed) {
                      return bed.bedKey === allocation.bedKey;
                    });

                    return (
                      <div
                        className={css(tableClasses.bodyRow, rowStyle, { padding: "8px 16px" })}
                        key={allocation.bedKey}
                      >
                        <div
                          className={TypographyStyle({
                            ellipsis: true,
                            fontSize: "14px",
                            lineHeight: 1,
                          })}
                        >
                          {bed?.roomName}
                        </div>
                        <div
                          className={TypographyStyle({
                            ellipsis: true,
                            fontSize: "14px",
                            lineHeight: 1,
                          })}
                        >
                          {bed?.bedName}
                        </div>
                        <form.Field name={`allocations[${index}].sensorKey`}>
                          {function (field) {
                            const errorMessage = getErrorMessage(field.state.meta.errors.at(0));
                            return (
                              <Tooltip.Root
                                closeDelay={100}
                                openDelay={100}
                                positioning={{ placement: "top" }}
                              >
                                <Tooltip.Trigger asChild>
                                  <DropDownList
                                    className={
                                      errorMessage
                                        ? css({
                                            backgroundColor: "destructive",
                                            color: "destructive-foreground",
                                          })
                                        : undefined
                                    }
                                    onChange={function (event) {
                                      console.log("aiueo", { v: event.target.value });
                                      field.handleChange(
                                        (event.target.value || null) as SensorKey | null,
                                      );
                                    }}
                                    value={field.state.value ?? ""}
                                  >
                                    <option value="">{t("%None.0%")}</option>
                                    {sensors
                                      .filter(function (sensor) {
                                        return (
                                          sensor.sensorKey === field.state.value ||
                                          !selectedSensorKeys.has(sensor.sensorKey)
                                        );
                                      })
                                      .map(function (sensor) {
                                        return (
                                          <option key={sensor.sensorKey} value={sensor.sensorKey}>
                                            {sensor.name}
                                            {sensor.type === SENSOR_TYPE.AAMS &&
                                              " (" + sensor.macAddress + ")"}
                                            {sensor.type === SENSOR_TYPE.ANSIEL &&
                                              " (" + sensor.serialNumber + ")"}
                                          </option>
                                        );
                                      })}
                                  </DropDownList>
                                </Tooltip.Trigger>
                                {errorMessage && (
                                  <Portal>
                                    <Tooltip.Positioner>
                                      <Tooltip.Content className={tooltipClasses.content}>
                                        <Tooltip.Arrow className={tooltipClasses.arrow}>
                                          <Tooltip.ArrowTip className={tooltipClasses.arrowTip} />
                                        </Tooltip.Arrow>
                                        <p className={TypographyStyle({ fontSize: "14px" })}>
                                          {errorMessage}
                                        </p>
                                      </Tooltip.Content>
                                    </Tooltip.Positioner>
                                  </Portal>
                                )}
                              </Tooltip.Root>
                            );
                          }}
                        </form.Field>
                        <form.Field name={`allocations[${index}].userKey`}>
                          {function (subfield) {
                            return (
                              <DropDownList
                                onChange={function (event) {
                                  subfield.handleChange(
                                    (event.target.value || null) as UserKey | null,
                                  );
                                }}
                                value={subfield.state.value ?? ""}
                              >
                                <option value="">{t("%None.0%")}</option>
                                {residents
                                  .filter(function (resident) {
                                    return (
                                      resident.userKey === subfield.state.value ||
                                      !selectedUserKeys.has(resident.userKey)
                                    );
                                  })
                                  .map(function (resident) {
                                    return (
                                      <option key={resident.userKey} value={resident.userKey}>
                                        {resident.name}
                                      </option>
                                    );
                                  })}
                              </DropDownList>
                            );
                          }}
                        </form.Field>
                      </div>
                    );
                  })}

                  {beds.length === 0 &&
                    (bedInfiniteQuery.isLoading ? (
                      Array.from({ length: 5 }).map(function (_, index) {
                        return (
                          <div
                            className={css(tableClasses.bodyRow, rowStyle, { padding: "16px" })}
                            key={index}
                          >
                            <div className={css(skeletonStyle, { maxWidth: "96px" })} />
                            <div className={css(skeletonStyle, { maxWidth: "128px" })} />
                            <div />
                          </div>
                        );
                      })
                    ) : (
                      <div className={css(tableClasses.bodyRow, { padding: "16px" })}>
                        <p className={css({ fontWeight: 500 })}>{t("%NoItems.0%")}</p>
                      </div>
                    ))}
                </div>
              );
            }}
          </form.Field>
        </form>
      </div>
    </main>
  );
}
